import { AfterViewInit, Component, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { divide as _divide, get as _get, round as _round } from 'lodash';
import { Observable, Subject } from 'rxjs';
import { filter, first, map, take, tap, withLatestFrom } from 'rxjs/operators';

import { PolicySelectors } from '@amfam/policy/data-access';
// Misc
import { CoverageChangeModel, QuoteModel, RiskModel } from '@amfam/policy/models';
import { ApiStatus } from '@amfam/shared/models';
import { CopyService } from '@amfam/shared/utility/shared-services';
import {
  DockingBarService,
  ErrorLevels,
  ErrorObj,
  LoadingSpinnerService,
  PageTitleService
} from '@amfam/ui-kit';

import * as fromRoot from '../../../core/store';
import * as coverageChangeSelector from '../../+state';
import {
  CoverageChangeRequestResetStateAction,
  CoverageChangeSetSelectedLimitsRequestAction,
  CoverageChangesGetQuoteRequestAction,
  SendCoverageAnalyticsAction
} from '../../+state/coverage-change.actions';

@Component({
  selector: 'ds-coverage-change-get-quote',
  templateUrl: './coverage-change-get-quote.component.html',
  styleUrls: ['./coverage-change-get-quote.component.scss'],
  standalone: false
})
export class CoverageChangeGetQuoteComponent implements OnInit, AfterViewInit {
  private vehicleId: string;

  // Form elements
  coverageChangeQuoteForm: UntypedFormGroup;
  comprehensiveCoverageInput: AbstractControl;
  collisionCoverageInput: AbstractControl;
  eraInput: AbstractControl;
  rentalInput: AbstractControl;
  comprehensiveCoverageInputValues$: Observable<string[]> = this.store.select(
    coverageChangeSelector.selectComprehensiveCoverageLimits
  );
  collisionCoverageInputValues$: Observable<string[]> = this.store.select(
    coverageChangeSelector.selectCollisionCoverageLimits
  );
  rentalReimbursementsInputValues$: Observable<string[]> = this.store.select(
    coverageChangeSelector.selectRentalCoverageLimits
  );

  currentQuote: QuoteModel; // TODO - Replace with API result
  newQuote: QuoteModel; // TODO - Replace with API result
  currentQuote$: Observable<QuoteModel>;
  agentPhoneNumber$: Observable<string>;
  eraExisting = false; // TODO - Replace with API result
  eraCost = 15; // TODO - Replace with ?
  rentalExisting = false; // TODO - Replace with API result
  existingRentalCoverage: string;
  rentalCost = 15; // TODO - Replace with ?
  showRentalValues = false;
  // Localized Loading spinner text
  loadingMsgText = 'Getting new quote...';
  // Determine if we're loading
  dataLoading = false; // TODO replace with observable of course
  changesPending = false;
  currentVehicle$: Observable<RiskModel>;
  currentVehicle: RiskModel;
  showComprehensiveInfo = false;
  showHelpText = 'Show help text';
  hideHelpText = 'Hide help text';
  comprehensiveInfoInfoHelpIconText = this.showHelpText;
  showCollisonInfo = false;
  collisionInfoInfoHelpIconText = this.showHelpText;
  additionalStyle: string;

  public quoteRequestError$: Observable<ApiStatus>;
  // Private variables
  private stop$: Subject<void> = new Subject<void>();
  public policyErrorList = new Array(this.getPoliciesError(this.copyService));
  public buttonDisabled = true;

  constructor(
    private store: Store<fromRoot.RootState>,
    private formBuilder: UntypedFormBuilder,
    private pageTitle: PageTitleService,
    private dockingBarService: DockingBarService,
    private route: ActivatedRoute,
    private spinner: LoadingSpinnerService,
    private copyService: CopyService
  ) {}

  ngOnInit() {
    this.store.dispatch(new CoverageChangeRequestResetStateAction());

    this.currentVehicle$ = this.route.params.pipe(
      map(params => params['id']),
      take(1),
      withLatestFrom(this.store.select(fromRoot.getReplaceVehicleRisks)),
      map(([vehicleId, vehicles]) => vehicles.find(risk => risk.id === vehicleId)),
      tap(currentVehicle => {
        this.pageTitle.set('Make Changes to ' + _get(currentVehicle, 'description', ''));
        this.dockingBarService.registerHeading(
          'Make Changes to ' + _get(currentVehicle, 'description', '')
        );
      })
    );

    /**
     * AS : Setup the existing quote model, along with other variables
     */
    this.currentQuote$ = this.currentVehicle$.pipe(
      take(1),
      withLatestFrom(this.store.select(PolicySelectors.selectAdvanceAutoPolicies)),
      map(([currentVehicle, policies]) =>
        policies.find(policy => {
          return _get(policy, 'policyNumber') === currentVehicle.policyNumber;
        })
      ),
      map((currentPolicy): QuoteModel => {
        const currentTermMonths = Number(currentPolicy.termDescription.split(' ')[0]);
        return {
          description: 'Current',
          fullCost: Number(_get(currentPolicy, 'totalPremium', 0)).toFixed(2),
          monthlyCost: _round(_divide(currentPolicy.totalPremium, currentTermMonths), 2).toFixed(2),
          policyTermMonths: currentTermMonths + ' months'
        };
      })
    );

    this.currentVehicle$.subscribe(vehicle => {
      this.currentVehicle = vehicle;
      this.setupForm(vehicle);
    });

    this.quoteRequestError$ = this.store
      .select(coverageChangeSelector.selectCoverageChangePageError)
      .pipe(
        filter(error => !!error),
        withLatestFrom(
          this.store.select(
            fromRoot.getAgentInformationByVehicleId,
            _get(this.currentVehicle, 'vehicleId', '')
          )
        ),
        map(([error, agent]) => {
          if (error) {
            this.spinner.stop();
            if (<number>_get(error, 'status.code') === 400) {
              this.policyErrorList = new Array(
                this.getPoliciesError(
                  this.copyService,
                  'policy.changeCoveragePCError',
                  _get(agent, 'phones[0].areaCode') +
                    '-' +
                    _get(agent, 'phones[0].phoneNumber', '').substring(0, 3) +
                    '-' +
                    _get(agent, 'phones[0].phoneNumber').substring(3, 7)
                )
              );
            }
          }
          return error;
        })
      );

    this.agentPhoneNumber$ = this.store
      .select(fromRoot.getAgentInformationByVehicleId, _get(this.currentVehicle, 'vehicleId', ''))
      .pipe(
        map(
          agent =>
            _get(agent, 'phones[0].areaCode') +
            '-' +
            _get(agent, 'phones[0].phoneNumber', '').substring(0, 3) +
            '-' +
            _get(agent, 'phones[0].phoneNumber', '').substring(3, 7)
        )
      );
  }

  ngAfterViewInit() {
    this.store.dispatch(
      new SendCoverageAnalyticsAction({
        pageAnalyticName: 'Start'
      })
    );
  }
  onCoverageChange() {
    const rentalReimbursementsInput = this.coverageChangeQuoteForm.get('rentalReimbursementsInput');
    const comprehensiveCoverageInput = this.coverageChangeQuoteForm.get(
      'comprehensiveCoverageInput'
    );
    const collisionCoverageInput = this.coverageChangeQuoteForm.get('collisionCoverageInput');

    if (
      rentalReimbursementsInput.value &&
      comprehensiveCoverageInput.value &&
      collisionCoverageInput.value &&
      this.rentalInput === rentalReimbursementsInput.value &&
      this.comprehensiveCoverageInput === comprehensiveCoverageInput.value &&
      this.collisionCoverageInput === collisionCoverageInput.value
    ) {
      this.buttonDisabled = true;
    } else {
      this.buttonDisabled = false;
    }
  }
  private setupForm(currentVehicle: RiskModel) {
    this.rentalExisting = !!this.getERAandRentalCurrentDeductible('PARentalCov', currentVehicle);
    if (this.rentalExisting) {
      this.existingRentalCoverage = this.getRentalLimit('PARentalCov', currentVehicle);
    }

    this.coverageChangeQuoteForm = this.formBuilder.group({
      comprehensiveCoverageInput: [
        this.getCurrentDeductible('PAComprehensiveCov', currentVehicle),
        Validators.compose([Validators.required])
      ],
      collisionCoverageInput: [
        this.getCurrentDeductible('PACollisionCov', currentVehicle),
        Validators.compose([Validators.required])
      ],
      eraInput: [
        !!this.getERAandRentalCurrentDeductible('PAEmergencyRoadServiceCov_af', currentVehicle)
      ],
      rentalInput: [!!this.getCurrentDeductible('PARentalCov', currentVehicle)],
      rentalReimbursementsInput: [this.existingRentalCoverage || '']
    });

    this.eraExisting = !!this.getERAandRentalCurrentDeductible(
      'PAEmergencyRoadServiceCov_af',
      currentVehicle
    );

    if (this.rentalExisting) {
      this.coverageChangeQuoteForm
        .get('rentalReimbursementsInput')
        .setValidators([Validators.required]);
      this.coverageChangeQuoteForm.get('rentalReimbursementsInput').updateValueAndValidity();
    }

    /**
     * AS: This should always be the last statement inside setup form, this sets up the
     * subscription to listen for a change in the form inputs.
     */
    this.coverageChangeQuoteForm.valueChanges.pipe(first()).subscribe(() => {
      this.buttonDisabled = false;
    });
    this.rentalInput = this.coverageChangeQuoteForm.get('rentalReimbursementsInput').value;
    this.comprehensiveCoverageInput = this.coverageChangeQuoteForm.get(
      'comprehensiveCoverageInput'
    ).value;
    this.collisionCoverageInput = this.coverageChangeQuoteForm.get('collisionCoverageInput').value;
  }

  private getCurrentDeductible(code: string, vehicle: RiskModel): string {
    const matchingCoverage = _get(vehicle, 'vehicleCoverages', []).find(
      coverage => coverage.code === code
    );
    return _get(matchingCoverage, 'deductibleTerms[0].value', '');
  }

  private getERAandRentalCurrentDeductible(code: string, vehicle: RiskModel): boolean {
    return _get(vehicle, 'vehicleCoverages', []).find(coverage => coverage.code === code);
  }

  private getRentalLimit(code: string, vehicle: RiskModel): string {
    const matchingCoverage = _get(vehicle, 'vehicleCoverages', []).find(
      coverage => coverage.code === code
    );
    const dailyLimit = _get(matchingCoverage, 'limitTerms', []).find(
      limit => limit.description === 'Daily Limit'
    );
    const maxLimit = _get(matchingCoverage, 'limitTerms', []).find(
      limit => limit.description === 'Maximum'
    );
    return `${dailyLimit.value}/${maxLimit.value}`;
  }

  rentalToggle(rentalValue: boolean) {
    this.showRentalValues = rentalValue;
    if (this.showRentalValues) {
      this.coverageChangeQuoteForm
        .get('rentalReimbursementsInput')
        .setValidators([Validators.required]);
    } else {
      this.coverageChangeQuoteForm.get('rentalReimbursementsInput').setValidators([]);
    }
    this.coverageChangeQuoteForm.get('rentalReimbursementsInput').updateValueAndValidity();
  }

  getQuote(currentVehicle: RiskModel) {
    this.spinner.start({
      secondaryMessage: `We’re calculating your quote. This may take a moment.`,
      blockActions: true
    });
    this.store.dispatch(
      new CoverageChangesGetQuoteRequestAction(this.buildSelectionPayload(currentVehicle))
    );

    this.store.dispatch(
      new CoverageChangeSetSelectedLimitsRequestAction(this.buildPayloadToPersist(currentVehicle))
    );

    /**
     * @description : Dispatch analytics that the check box for rental coverage was unchecked.
     */
    if (!this.showRentalValues) {
      this.store.dispatch(
        new SendCoverageAnalyticsAction({
          buttonAnalyticName: 'CancelCoverageRR'
        })
      );
    }

    /**
     * @description : Dispatch analytics that the check box for emergency roadside coverage was unchecked.
     */
    if (!this.coverageChangeQuoteForm.get('eraInput').value) {
      this.store.dispatch(
        new SendCoverageAnalyticsAction({
          buttonAnalyticName: 'CancelCoverageERS'
        })
      );
    }
  }

  private buildSelectionPayload(currentVehicle: RiskModel): CoverageChangeModel {
    const riskCoverages: Array<any> = [
      {
        code: 'PAComprehensiveCov',
        selected: true,
        terms: [
          {
            code: 'PACompDeductible',
            selectedValue: this.coverageChangeQuoteForm.get('comprehensiveCoverageInput').value
          }
        ]
      },
      {
        code: 'PACollisionCov',
        selected: true,
        terms: [
          {
            code: 'PACollDeductible',
            selectedValue: this.coverageChangeQuoteForm.get('collisionCoverageInput').value
          }
        ]
      }
    ];

    if (this.showRentalValues || this.rentalExisting) {
      riskCoverages.push({
        code: 'PARentalCov',
        selected: true,
        terms: [
          {
            code: 'PARental',
            selectedValue: this.coverageChangeQuoteForm.get('rentalReimbursementsInput').value
          }
        ]
      });
    }
    if (this.coverageChangeQuoteForm.get('eraInput').value && !this.eraExisting) {
      riskCoverages.push({
        code: 'PAEmergencyRoadServiceCov_af',
        selected: true
      });
    }

    const effectiveDate = new Date();
    effectiveDate.setDate(effectiveDate.getDate() + 1);

    return {
      effectiveDate: effectiveDate.toISOString(),
      riskCoverages: riskCoverages,
      vehicleIdentifier: this.currentVehicle.vehicleId,
      Vin: this.currentVehicle.vin,
      policyNumber: this.currentVehicle.policyNumber
    };
  }

  private buildPayloadToPersist(currentVehicle: RiskModel): CoverageChangeModel {
    const riskCoverages: Array<any> = [];
    if (
      this.getCurrentDeductible('PAComprehensiveCov', currentVehicle) !==
      this.coverageChangeQuoteForm.get('comprehensiveCoverageInput').value
    ) {
      riskCoverages.push({
        code: 'PAComprehensiveCov',
        selected: true,
        terms: [
          {
            code: 'PACompDeductible',
            selectedValue: this.coverageChangeQuoteForm.get('comprehensiveCoverageInput').value
          }
        ]
      });
    }
    if (
      this.getCurrentDeductible('PACollisionCov', currentVehicle) !==
      this.coverageChangeQuoteForm.get('collisionCoverageInput').value
    ) {
      riskCoverages.push({
        code: 'PACollisionCov',
        selected: true,
        terms: [
          {
            code: 'PACollDeductible',
            selectedValue: this.coverageChangeQuoteForm.get('collisionCoverageInput').value
          }
        ]
      });
    }

    if (this.showRentalValues || this.rentalExisting) {
      if (
        this.existingRentalCoverage !==
        this.coverageChangeQuoteForm.get('rentalReimbursementsInput').value
      ) {
        riskCoverages.push({
          code: 'PARentalCov',
          selected: true,
          terms: [
            {
              code: 'PARental',
              selectedValue: this.coverageChangeQuoteForm.get('rentalReimbursementsInput').value
            }
          ]
        });
      }
    }
    if (this.coverageChangeQuoteForm.get('eraInput').value && !this.eraExisting) {
      riskCoverages.push({
        code: 'PAEmergencyRoadServiceCov_af',
        selected: true
      });
    }

    return {
      effectiveDate: new Date().toISOString(),
      riskCoverages: riskCoverages,
      vehicleIdentifier: this.currentVehicle.vehicleId,
      Vin: this.currentVehicle.vin,
      policyNumber: this.currentVehicle.policyNumber
    };
  }

  toggleComprehensiveInfo() {
    this.showComprehensiveInfo = !this.showComprehensiveInfo;
    this.additionalStyle = this.showComprehensiveInfo ? 'extra-pad-top' : '';
    this.comprehensiveInfoInfoHelpIconText = this.showComprehensiveInfo
      ? this.hideHelpText
      : this.showHelpText;
  }

  toggleCollisionInfo() {
    this.showCollisonInfo = !this.showCollisonInfo;
    this.collisionInfoInfoHelpIconText = this.showCollisonInfo
      ? this.hideHelpText
      : this.showHelpText;
  }

  /**
   * @description Function which fetches the error from the copy service and returns it in the error obj format.
   */
  private getPoliciesError(
    copyService: CopyService,
    path = 'policy.defaultErrorMessage',
    agentNumber?: string
  ): ErrorObj {
    return {
      errorMessage: copyService
        ? agentNumber
          ? copyService.getCopy(path, { agentPhone: agentNumber })
          : copyService.getCopy(path)
        : '',
      errorLevel: ErrorLevels.HIGH
    };
  }
}
